import OutlineTitle from "@/components/OutlineTitle";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { paymentsAPI } from "@/lib/api";
import { money } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Package, CheckCircle2, Truck, ShieldAlert, Copy, AlertTriangle, ShoppingCart, BanknoteArrowUp } from "lucide-react";
import OrderStatusTimeline from "@/components/OrderStatusTimeline";

const STATUS_COLORS = {
  paid: "bg-primary/20 text-primary",
  shipped: "bg-accent/20 text-accent",
  delivered: "bg-emerald-500/20 text-emerald-400",
  completed: "bg-emerald-500/20 text-emerald-400",
  cancelled: "bg-destructive/20 text-destructive",
  refunded: "bg-muted text-muted-foreground",
  disputed: "bg-amber-500/20 text-amber-400",
};

function ShipDialog({ order, open, onOpenChange, onShipped }) {
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("USPS");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!tracking.trim()) { toast.error("Tracking number is required"); return; }
    setBusy(true);
    try {
      await paymentsAPI.updateOrderStatus(order.id, {
        status: "shipped",
        tracking_number: tracking.trim(),
        notes: notes ? `${carrier}: ${notes}` : `Shipped via ${carrier}`,
      });
      toast.success("Marked as shipped — buyer notified with tracking #");
      onShipped?.();
      onOpenChange(false);
      setTracking(""); setNotes("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark shipped");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card">
        <DialogHeader>
          <DialogTitle>Mark &ldquo;{order?.listing_title}&rdquo; as shipped</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Tracking number <span className="text-primary">*</span></Label>
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. 9400 1112 3456 7890" data-testid="ship-dialog-tracking" />
          </div>
          <div className="space-y-1.5">
            <Label>Carrier</Label>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="USPS, UPS, FedEx, DHL" data-testid="ship-dialog-carrier" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the buyer should know" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !tracking.trim()} className="bg-primary text-primary-foreground" data-testid="ship-dialog-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Truck className="mr-2 h-4 w-4" /> Mark shipped</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDialog({ order, open, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Please describe the issue"); return; }
    setBusy(true);
    try {
      await paymentsAPI.openDispute(order.id, { reason: reason.trim(), details: details.trim() });
      toast.success("Dispute opened — SRC will review");
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open dispute");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card">
        <DialogHeader>
          <DialogTitle>Open a dispute</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Only open a dispute if you haven&apos;t received the item, it&apos;s not as described, or you have another serious problem.
          SRC will review the case and reach a fair resolution.
        </p>
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label>Reason <span className="text-primary">*</span></Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Not received, damaged, not as described..." data-testid="dispute-reason" />
          </div>
          <div className="space-y-1.5">
            <Label>Details</Label>
            <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Additional context to help SRC review" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} variant="destructive" data-testid="dispute-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldAlert className="mr-2 h-4 w-4" /> Open dispute</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderCard({ order, role, onChanged }) {
  const [shipOpen, setShipOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);

  const copyTracking = () => {
    if (!order.tracking_number) return;
    navigator.clipboard?.writeText(order.tracking_number);
    toast.success("Tracking number copied");
  };

  const isBuyer = role === "buyer";
  const isSeller = role === "seller";
  const canConfirm = isBuyer && order.escrow_status === "held" && ["paid", "shipped"].includes(order.status);
  const canDispute = isBuyer && order.escrow_status === "held" && !order.dispute_reason;
  const canShip = isSeller && order.status === "paid" && order.escrow_status === "held";

  return (
    <Card className="border-border bg-card p-4" data-testid={`order-card-${order.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium">{order.listing_title}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(order.created_at).toLocaleDateString()} · {money((order.amount || 0) / 100, order.currency?.toUpperCase() || "USD")}
              {isBuyer && order.seller_username && <> · Seller: {order.seller_username}</>}
              {isSeller && order.buyer_username && <> · Buyer: {order.buyer_username}</>}
            </p>
          </div>
        </div>
        <Badge className={`border-0 ${STATUS_COLORS[order.escrow_status] || STATUS_COLORS[order.status] || "bg-secondary"}`}>
          {order.escrow_status === "disputed" ? "Disputed" : (order.status || "—")}
        </Badge>
      </div>

      <OrderStatusTimeline order={order} className="mt-4" />

      {order.tracking_number && (
        <div className="mt-3 flex items-center justify-between rounded-md border border-border bg-secondary/40 p-2.5 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <Truck className="h-4 w-4 flex-shrink-0 text-primary" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Tracking</p>
              <p className="truncate font-mono" data-testid={`order-tracking-${order.id}`}>{order.tracking_number}</p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={copyTracking} data-testid={`order-tracking-copy-${order.id}`}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {order.dispute_reason && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
          <div>
            <p className="font-medium text-amber-400">Dispute open — under SRC review</p>
            <p className="text-xs text-muted-foreground">Reason: {order.dispute_reason}</p>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {canShip && (
          <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => setShipOpen(true)} data-testid={`order-ship-${order.id}`}>
            <Truck className="mr-1.5 h-4 w-4" /> Mark shipped + tracking
          </Button>
        )}
        {canConfirm && (
          <Button size="sm" className="bg-primary text-primary-foreground" onClick={async () => {
            try { await paymentsAPI.confirmDelivery(order.id); toast.success("Delivery confirmed — funds released"); onChanged?.(); }
            catch (e) { toast.error(e.response?.data?.detail || "Could not confirm delivery"); }
          }} data-testid={`order-confirm-${order.id}`}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" /> Confirm delivery
          </Button>
        )}
        {canDispute && (
          <Button size="sm" variant="outline" onClick={() => setDisputeOpen(true)} data-testid={`order-dispute-${order.id}`}>
            <ShieldAlert className="mr-1.5 h-4 w-4" /> Open dispute
          </Button>
        )}
      </div>

      <ShipDialog order={order} open={shipOpen} onOpenChange={setShipOpen} onShipped={onChanged} />
      <DisputeDialog order={order} open={disputeOpen} onOpenChange={setDisputeOpen} onDone={onChanged} />
    </Card>
  );
}

export default function Orders() {
  const navigate = useNavigate();
  const [role, setRole] = useState("buyer");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    paymentsAPI
      .getMyOrders({ role })
      .then((res) => setOrders(res.data?.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => load(), [load]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <OutlineTitle>My Orders</OutlineTitle>
      </div>

      <Tabs value={role} onValueChange={setRole}>
        <TabsList>
          <TabsTrigger value="buyer" data-testid="orders-tab-purchases"><ShoppingCart className="mr-1.5 h-4 w-4" /> Purchases</TabsTrigger>
          <TabsTrigger value="seller" data-testid="orders-tab-sales"><BanknoteArrowUp className="mr-1.5 h-4 w-4" /> Sales</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : orders.length ? (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} role={role} onChanged={load} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
          <Package className="mb-3 h-12 w-12 text-muted-foreground" />
          <p className="font-medium">No {role === "buyer" ? "purchases" : "sales"} yet</p>
          <Button variant="outline" className="mt-3" onClick={() => navigate("/marketplace")}>Browse marketplace</Button>
        </div>
      )}
    </div>
  );
}
